/**
 * Typed IPC channel definitions shared between main and renderer processes.
 *
 * Convention:
 * - Request/response channels use `invoke` (renderer calls, main responds)
 * - Push channels use `on` (main pushes to renderer)
 */
/**
 * One complete, canonical `FeatureFlagsSnapshot` default. Stubs, dev
 * mocks, and test fixtures MUST spread from this and override only the
 * fields the case cares about — never hand-enumerate every field.
 *
 * Why: `FeatureFlagsSnapshot` is an exhaustive type; a hand-enumerated
 * mock breaks the moment the type gains a field, and the break only
 * surfaces in the cross-platform `typecheck:tests` leg (the plain
 * `typecheck` skips test files), three platforms deep in CI. Spreading
 * from this default makes a field addition invisible to every mock.
 * (Crucible #2197; rule: exhaustive-mocks-from-default-and-test-typecheck.)
 *
 * Values mirror the shipped production defaults documented on each
 * field above: opt-OUT flags default ON, opt-IN flags default OFF. Every
 * optional field is set explicitly so the default is genuinely complete.
 */
export const DEFAULT_FEATURE_FLAGS = {
    interviewEngineV1: true,
    agentTransparency: true,
    lazyDashboardLoad: true,
    foundryPrototypes: false,
    levelDesignPro: false,
    cloudVmV1: false,
    cloudProjectPlacement: false,
    newUi: true,
    craftMode: false,
    // crucible#3313 — the one collapsed intake key (formerly the identical
    // interviewIntegrated / intakePreview / northStar trio). Default OFF here:
    // this constant models the no-env baseline the SDK/cloud lanes see.
    intake: false,
    livePreview: true,
    scaffoldFirst: false,
    designTab: true,
    tileImageUpload: true,
    artTab: false,
    // crucible#3052 Bugbot MEDIUM (6497f0cd) — rides `isIntakeEnabled()`, which
    // defaults OFF with no env set (same reasoning as the GUI-shared mirror).
    buildTimeAssetReview: false,
    createSplit: false,
};
// ---------------------------------------------------------------------------
// Asset Forge 0.1.24 native model v2
// ---------------------------------------------------------------------------
/** The complete renderer-visible allowlist for native model v2 operations. */
export const ART_MODEL_V2_INVOKE_CHANNELS = [
    'art:model:list',
    'art:model:add',
    'art:model:detail',
    'art:model:versions',
    'art:model:version',
    'art:model:run',
    'art:model:validation',
    'art:model:preview',
    'art:model:generate',
    'art:model:revise',
    'art:model:approve',
    'art:model:reject',
    'art:model:unapprove',
    'art:model:retry',
];
/**
 * ID prefix for the chat message synthesised from the Create dialog's
 * "Initial AI Prompt" field. Set in `main/chat-history.ts`'s
 * `persistInitialPromptAsChatMessage` and matched in
 * `renderer/pages/Workspace.tsx`'s `chat:load` effect to auto-send the
 * persisted prompt to the agent (issue #145). The shared constant
 * keeps the two call sites in lockstep — historically this was a magic
 * string in both places, and forking would silently break the
 * auto-send while leaving everything else looking healthy.
 */
export const INITIAL_PROMPT_MESSAGE_ID_PREFIX = 'initial-prompt-';
/**
 * Hardcoded allowlist of repos the `/file-bug` SKILL may target. v1
 * keeps this in code (no `CrucibleConfig` field, no Settings UI) per
 * the brief; v1.1 may surface it as a configurable list. Both the
 * SKILL prose and the main-side `bug:file` handler validate against
 * this constant, so a SKILL that drifts (e.g. by misreading
 * frontmatter) still gets refused by main with a structured error.
 */
export const BUG_REPO_ALLOWLIST = [
    'Volley-Inc/crucible',
    'Volley-Inc/hello-weekend',
];
//# sourceMappingURL=ipc-channels.js.map